import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ListTodo,
  ArrowLeft,
  Plus,
  Trash2,
  FileText,
  Home,
  CreditCard,
  Briefcase,
  MoreHorizontal,
  CheckCircle,
  Circle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TasksPage = ({ user }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    category: "documents",
    due_date: "",
    country: user?.target_country || "ireland",
  });

  const categories = [
    { value: "visa", label: "Visa & Immigration", icon: FileText, color: "#4A6C6F" },
    { value: "documents", label: "Documents", icon: FileText, color: "#D97757" },
    { value: "housing", label: "Housing", icon: Home, color: "#48BB78" },
    { value: "banking", label: "Banking", icon: CreditCard, color: "#ED8936" },
    { value: "employment", label: "Employment", icon: Briefcase, color: "#4299E1" },
    { value: "other", label: "Other", icon: MoreHorizontal, color: "#718096" },
  ];

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API}/tasks`, { credentials: "include" });
      if (response.ok) {
        setTasks(await response.json());
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title) {
      toast.error("Please enter a task title");
      return;
    }

    try {
      const response = await fetch(`${API}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newTask),
      });

      if (response.ok) {
        toast.success("Task added!");
        setDialogOpen(false);
        setNewTask({
          title: "",
          description: "",
          category: "documents",
          due_date: "",
          country: user?.target_country || "ireland",
        });
        fetchTasks();
      } else {
        toast.error("Failed to add task");
      }
    } catch (error) {
      toast.error("Error adding task");
    }
  };

  const handleToggleTask = async (taskId, completed) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}?completed=${!completed}`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        fetchTasks();
      }
    } catch (error) {
      toast.error("Error updating task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Task deleted");
        fetchTasks();
      }
    } catch (error) {
      toast.error("Error deleting task");
    }
  };

  const filteredTasks =
    filter === "all"
      ? tasks
      : filter === "completed"
      ? tasks.filter((t) => t.completed)
      : filter === "pending"
      ? tasks.filter((t) => !t.completed)
      : tasks.filter((t) => t.category === filter);

  const completedCount = tasks.filter((t) => t.completed).length;
  const progress = tasks.length > 0 ? (completedCount / tasks.length) * 100 : 0;

  const getCategoryIcon = (categoryValue) => {
    const cat = categories.find((c) => c.value === categoryValue);
    return cat?.icon || MoreHorizontal;
  };

  const getCategoryColor = (categoryValue) => {
    const cat = categories.find((c) => c.value === categoryValue);
    return cat?.color || "#718096";
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                <ListTodo className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">Moving Checklist</span>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-task-btn"
                className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Task</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium text-[#1A202C] mb-2 block">Task Title</label>
                  <Input
                    data-testid="task-title-input"
                    placeholder="e.g., Apply for visa"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A202C] mb-2 block">Description</label>
                  <Input
                    data-testid="task-desc-input"
                    placeholder="Optional details"
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A202C] mb-2 block">Category</label>
                  <Select
                    value={newTask.category}
                    onValueChange={(v) => setNewTask({ ...newTask, category: v })}
                  >
                    <SelectTrigger data-testid="task-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A202C] mb-2 block">Due Date</label>
                  <Input
                    data-testid="task-date-input"
                    type="date"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  />
                </div>
                <Button
                  data-testid="save-task-btn"
                  onClick={handleAddTask}
                  className="w-full bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
                >
                  Add Task
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#4A6C6F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Progress */}
            <Card data-testid="progress-card" className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl mb-8">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-[#1A202C]">Your Progress</h3>
                    <p className="text-[#718096]">
                      {completedCount} of {tasks.length} tasks completed
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-[#4A6C6F]">{Math.round(progress)}%</span>
                  </div>
                </div>
                <Progress value={progress} className="h-3" />
              </CardContent>
            </Card>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Button
                data-testid="filter-all"
                variant={filter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("all")}
                className={`rounded-full ${
                  filter === "all" ? "bg-[#4A6C6F] text-white" : "border-[#E2E8F0]"
                }`}
              >
                All ({tasks.length})
              </Button>
              <Button
                data-testid="filter-pending"
                variant={filter === "pending" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("pending")}
                className={`rounded-full ${
                  filter === "pending" ? "bg-[#ED8936] text-white" : "border-[#E2E8F0]"
                }`}
              >
                Pending ({tasks.filter((t) => !t.completed).length})
              </Button>
              <Button
                data-testid="filter-completed"
                variant={filter === "completed" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("completed")}
                className={`rounded-full ${
                  filter === "completed" ? "bg-[#48BB78] text-white" : "border-[#E2E8F0]"
                }`}
              >
                Completed ({completedCount})
              </Button>
            </div>

            {/* Tasks List */}
            <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
              <CardContent className="p-6">
                {filteredTasks.length === 0 ? (
                  <div className="text-center py-12 text-[#718096]">
                    <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No tasks yet</p>
                    <p className="text-sm">Click "Add Task" to start your checklist</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredTasks.map((task) => {
                      const Icon = getCategoryIcon(task.category);
                      const color = getCategoryColor(task.category);
                      return (
                        <div
                          key={task.task_id}
                          data-testid={`task-${task.task_id}`}
                          className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${
                            task.completed ? "bg-[#48BB78]/10" : "bg-[#F5F5F0]"
                          }`}
                        >
                          <button
                            data-testid={`toggle-${task.task_id}`}
                            onClick={() => handleToggleTask(task.task_id, task.completed)}
                            className="flex-shrink-0"
                          >
                            {task.completed ? (
                              <CheckCircle className="w-6 h-6 text-[#48BB78]" />
                            ) : (
                              <Circle className="w-6 h-6 text-[#CBD5E0]" />
                            )}
                          </button>
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${color}20` }}
                          >
                            <Icon className="w-5 h-5" style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`font-medium ${
                                task.completed ? "text-[#718096] line-through" : "text-[#1A202C]"
                              }`}
                            >
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-sm text-[#718096] truncate">{task.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span
                                className="text-xs px-2 py-0.5 rounded-full capitalize"
                                style={{ backgroundColor: `${color}20`, color }}
                              >
                                {task.category}
                              </span>
                              {task.due_date && (
                                <span className="text-xs text-[#718096]">Due: {task.due_date}</span>
                              )}
                            </div>
                          </div>
                          <Button
                            data-testid={`delete-task-${task.task_id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTask(task.task_id)}
                            className="text-[#E53E3E] hover:bg-[#E53E3E]/10 flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default TasksPage;
